import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import type { PublisherBillingSummary } from '../../types/selfService';
import { useAdminContext } from '../../components/self-service/admin/useAdminContext';
import { scopeForContext } from '../../components/self-service/admin/stationScopeRequest';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { ALL_STATIONS_SLUG } from '../../components/self-service/admin/lastAdminStation';
import { publisherBillingEnabled } from '../../components/self-service/admin/payoutsFeature';
import { rankOf } from '../../components/self-service/shared/roleRank';
import PayoutLedger from '../../components/self-service/billing/PayoutLedger';
import MonthSelector from '../../components/self-service/billing/MonthSelector';
import {
  buildMonthCsv,
  currentMonthKey,
  defaultLedgerMonth,
} from '../../components/self-service/billing/payoutLedgerMath';
import { apiClient } from '../../lib/apiClient';

// CTV-776 / ADR-0037: the publisher admin's ledger of money owed and paid.
//
// CTV-786 cut the page to the ledger alone: the header, the month selector
// with the CSV export, and the payout ledger by delivery month. The CTV-776
// status strip (payout method / still owed / next payout) and the explanatory
// subtitle are gone; readiness still drives the sidebar dot, which is where a
// publisher learns they have nothing connected.
//
// CTV-787 moved the Stripe payout-method setup CTV-776 mounted at the bottom
// to the Payout method tab of Station settings: reading rows widens across
// stations, writing configuration does not. Nothing on this page configures
// anything any more, which is why it needs no payout-status hook.
//
// Scope-flexible: under All stations the ledger is one table with a Station
// column (CTV-786), single-station scope is that station's month alone.

const PublisherBillingPage: React.FC = () => {
  const ctx = useAdminContext();
  const aggregated = ctx.mode === 'aggregated';
  const scopeSlug = aggregated ? ALL_STATIONS_SLUG : ctx.slug;
  const scope = scopeForContext(ctx);
  const scopeKey = JSON.stringify(scope);
  const isManagerPlus = rankOf(ctx.user?.rawRole) >= rankOf('manager');
  const enabled = publisherBillingEnabled();

  const [summary, setSummary] = React.useState<PublisherBillingSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // The delivery month on screen, mirrored to ?month=YYYY-MM so a pasted link
  // reproduces what the sender saw (same rule as the admin Billing page).
  // Absent from the URL, it defaults to the latest month with a row once the
  // ledger loads.
  const [params, setParams] = useSearchParams();
  const urlMonth = params.get('month');
  const monthParam = urlMonth && /^\d{4}-\d{2}$/.test(urlMonth) ? urlMonth : null;
  const setMonth = (m: string) => {
    const next = new URLSearchParams(params);
    next.set('month', m);
    setParams(next, { replace: true });
  };
  React.useEffect(() => {
    if (!enabled || !isManagerPlus) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await apiClient.post<PublisherBillingSummary>('/publisher-billing/summary', JSON.parse(scopeKey));
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load billing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeKey, enabled, isManagerPlus]);

  // Manager+ only, and only where the flag is on: every route this page
  // calls is manager-gated, and in production the surface is hidden until
  // Stripe Connect is live (see payoutsFeature.ts). Redirect rather than
  // strand a bookmark.
  if (!enabled || !isManagerPlus) {
    const landing = aggregated ? ctx.collapseSlug : ctx.slug;
    return <Navigate to={`/admin/${landing}`} replace />;
  }

  const stations = summary?.stations ?? [];
  const today = summary?.today ?? currentMonthKey() + '-01';
  const month = monthParam ?? (summary ? defaultLedgerMonth(stations, today) : currentMonthKey(today));
  const monthHasRows = stations.some((st) => st.campaigns.some((c) => c.months.some((m) => m.month === month)));

  const exportCsv = () => {
    const csv = buildMonthCsv(month, stations);
    const scopeName = ctx.mode === 'single' ? ctx.slug : 'all-stations';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-${scopeName}-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    // Deliberately NOT width-capped, unlike its sibling admin pages: the ledger
    // is a dozen-plus columns wide, and a cap meant zooming the browser out
    // bought the reader nothing. The container stayed put while the viewport
    // grew, so the last columns (Payout, Sales rep) kept scrolling out of
    // sight. Full width lets the whole row land on screen on a wide monitor or
    // at a zoomed-out ratio; the table's own overflow-x-auto still handles
    // narrow windows.
    <div data-testid="billing-page">
      <AdminPageHeader title="Billing" scopeMode="flexible" currentSlug={scopeSlug} />

      <section aria-label="Payout ledger" className="mb-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <MonthSelector value={month} onChange={setMonth} max={currentMonthKey(today)} />
          {/* The month's report laid out like the page, as CSV. Disabled when
              the month has nothing, so the file is never a lone header. */}
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || !!error || !monthHasRows}
            data-testid="export-month"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        <PayoutLedger stations={stations} grouped={aggregated} month={month} loading={loading} error={error} />
      </section>
    </div>
  );
};

export default PublisherBillingPage;
